import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Modal from "@commons/Modal";
import TextInput from "@commons/Input/Text";
import { usePage } from "@hooks/usePage";
import { toast } from "react-toastify";
import { AppError } from "@utils/errors/appError";
import { useEffect } from "react";
import { globalMutate } from "@utils/swr";
import { useGetCollarById } from "@services/device/collar/getById";
import { updateCollar } from "@services/device/collar/update";
import {
  collarEditSchema,
  collarEditDefaultValues,
  type CollarEditFormData,
} from "../../schemas/collarEditSchema";

function ModalEditCollar() {
  const { isModalOpen, closeModal, setIsLoading, setDataModal, dataModal } =
    usePage();
  const open = isModalOpen("editCollar");

  const id = dataModal?.id as string;
  const { data: collar, isLoading, error } = useGetCollarById(id);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<CollarEditFormData>({
    resolver: zodResolver(collarEditSchema),
    defaultValues: collarEditDefaultValues,
  });

  useEffect(() => {
    if (open && collar) {
      setValue("number", String(collar.number));
      setValue("responsibleTeam", collar.responsibleTeam || "");
    } else if (!open) {
      reset();
    }
  }, [open, collar, setValue, reset]);

  const onSubmit = async (data: CollarEditFormData) => {
    try {
      setIsLoading(true);

      await updateCollar(id, {
        number: data.number,
        responsibleTeam: data.responsibleTeam || undefined,
      });

      toast.success("Rastreador atualizado com sucesso");

      await globalMutate([
        "fetch-get-collars",
        "fetch-collars-geojson",
        `fetch-collar-${id}`,
      ]);

      handleClose();
    } catch (error) {
      const isAppError = error instanceof AppError;
      const title = isAppError ? error.message : "Erro ao atualizar Rastreador";
      toast.error(title);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    setDataModal({});
    closeModal();
  };

  if (!open) return null;

  return (
    <Modal
      open={open}
      heading="Editar Rastreador"
      onClose={handleClose}
      onConfirm={handleSubmit(onSubmit)}
      confirmLabel="Salvar"
      cancelLabel="Cancelar"
      className="max-w-2xl"
      isLoading={isLoading}
      error={!!error}
    >
      <form className="grid grid-cols-1 gap-4">
        <Controller
          name="number"
          control={control}
          render={({ field }) => (
            <TextInput
              {...field}
              label="Número do dispositivo"
              textSize="sm"
              fullWidth
              error={!!errors.number}
              helperText={errors.number?.message}
            />
          )}
        />

        <Controller
          name="responsibleTeam"
          control={control}
          render={({ field }) => (
            <TextInput
              {...field}
              label="Equipe Responsável"
              type="text"
              textSize="sm"
              fullWidth
              error={!!errors.responsibleTeam}
              helperText={errors.responsibleTeam?.message}
            />
          )}
        />
      </form>
    </Modal>
  );
}

export default ModalEditCollar;
